import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Header } from "../../../components/Header/Header";
import { Search } from "lucide-react";
import api, { getFreshAccessToken } from "../../chat/api";
import MyProjectFilterDropdown from "./MyProjectFilterDropdown";
import ProjectColumnDropdown from "./ProjectColumnDropdown";
import { emptyDateFilter } from "./projectDateUtils";
import useProjectsStore from "../../../store/useProjectsStore";
import { PROJECT_SOURCES, hasNoAccess } from "./projectAccess";

// Default shape for the Projects filters. `tags` is kept for backward compat with
// older persisted values; the Filters panel covers status + the two date ranges.
// `source` is NOT part of that panel — it is driven by the segmented switcher
// above the table, and defaults to the user's own work.
// Versioned so a value persisted by an earlier build of this feature cannot
// silently override the current default.
const PROJECTS_FILTERS_KEY = "projects_activeFilters_v2";

const makeDefaultFilters = () => ({
  source: PROJECT_SOURCES.ALL,
  statuses: [],
  bidEndDate: emptyDateFilter(),
  dateSubmitted: emptyDateFilter(),
  tags: [],
});

export const ColumnIcon = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="12" y1="3" x2="12" y2="21"></line>
  </svg>
);

const ProjectsLayout = ({ isMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.toLowerCase();

  // Shared State for Search, Filters
  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      const savedSearchQuery = localStorage.getItem("projects_searchQuery");
      return savedSearchQuery ? JSON.parse(savedSearchQuery) : "";
    } catch (error) {
      return "";
    }
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [activeFilters, setActiveFilters] = useState(() => {
    try {
      const savedFilters = localStorage.getItem(PROJECTS_FILTERS_KEY);
      // Merge over defaults so values persisted before the date filters existed
      // still get the new sections without breaking.
      return savedFilters
        ? { ...makeDefaultFilters(), ...JSON.parse(savedFilters) }
        : makeDefaultFilters();
    } catch (error) {
      return makeDefaultFilters();
    }
  });
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [tagsRefreshTrigger, setTagsRefreshTrigger] = useState(0);

  // Persist search query
  useEffect(() => {
    try {
      localStorage.setItem("projects_searchQuery", JSON.stringify(searchQuery));
    } catch (error) {
      console.error("Failed to save search query", error);
    }
  }, [searchQuery]);

  // Persist active filters
  useEffect(() => {
    try {
      localStorage.setItem(PROJECTS_FILTERS_KEY, JSON.stringify(activeFilters));
    } catch (error) {
      console.error("Failed to save active filters", error);
    }
  }, [activeFilters]);

  // 1. OPTIMISTIC UI: Initialize Columns from LocalStorage to prevent flashing
  const [columnPreferences, setColumnPreferences] = useState(() => {
    try {
      const savedColumns = localStorage.getItem("projects_columnPreferences");
      return savedColumns ? JSON.parse(savedColumns) : [];
    } catch (error) {
      console.error("Failed to parse saved columns", error);
      return [];
    }
  });

  // 2. BACKGROUND SYNC: Fetch from API to ensure we have the absolute latest config
  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const token = await getFreshAccessToken();
        const response = await api.get("/api/v1/rfp/column-preferences/?view=rfp_folders", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.columns) {
          setColumnPreferences(response.data.columns);
          // Keep LocalStorage perfectly synced with the API response
          localStorage.setItem("projects_columnPreferences", JSON.stringify(response.data.columns));
        }
      } catch (error) {
        console.error("Failed to fetch column preferences:", error);
      }
    };
    fetchColumns();
  }, []);

  const saveColumnPreferences = async (updatedColumns) => {
    try {
      const payload = {
        columns: updatedColumns.map((col, index) => ({
          name: col.name,
          visible: col.visible === true || col.visible === "true",
          order: index,
        })),
      };

      // 3. INSTANT UPDATE: Update state and LocalStorage immediately for snappy UX
      setColumnPreferences(payload.columns);
      localStorage.setItem("projects_columnPreferences", JSON.stringify(payload.columns));

      // Fire API Request in background
      const token = await getFreshAccessToken();
      await api.patch("/api/v1/rfp/column-preferences/?view=rfp_folders", payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Failed to save column preferences:", error);
    }
  };

  // ── Projects and Archived Projects each keep their OWN filter + search state
  //    (same parameters/UI, independent values) so changes on one tab don't leak
  //    into the other. ─────────────────────────────────────────────────────────
  const isArchivedTab = currentPath.includes("archived-projects");
  const activeTab = isArchivedTab ? "archived" : "projects";

  // Only org admins ever receive oversight rows, so their presence decides
  // whether the "No access yet" option is offered in the My access filter. A primitive
  // selector on purpose — returning an object here would allocate a new one on
  // every store update and re-render this layout forever.
  const hasOrgProjects = useProjectsStore((state) => state.projects.some(hasNoAccess));

  // If those rows go away while the user is filtered to them — all shared with
  // the admin, the admin lost the role, the workspace changed — the option
  // disappears from the list and would strand them on an empty table filtered by
  // a rule they can no longer see or undo. Snap back to All.
  useEffect(() => {
    if (isArchivedTab || hasOrgProjects) return;
    setActiveFilters((prev) =>
      prev.source === PROJECT_SOURCES.ORGANIZATION
        ? { ...prev, source: PROJECT_SOURCES.ALL }
        : prev
    );
  }, [isArchivedTab, hasOrgProjects]);

  // Archived Projects: its own filter + search state, same shape as Projects'.
  // No column-preference state — the archived table has a fixed column set (MVP scope).
  const [archivedFilters, setArchivedFilters] = useState(() => {
    try {
      const saved = localStorage.getItem("archivedprojects_activeFilters");
      return saved ? { ...makeDefaultFilters(), ...JSON.parse(saved) } : makeDefaultFilters();
    } catch (error) {
      return makeDefaultFilters();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("archivedprojects_activeFilters", JSON.stringify(archivedFilters));
    } catch (error) {
      console.error("Failed to save archived filters", error);
    }
  }, [archivedFilters]);

  const [archivedSearchQuery, setArchivedSearchQuery] = useState(() => {
    try {
      const saved = localStorage.getItem("archivedprojects_searchQuery");
      return saved ? JSON.parse(saved) : "";
    } catch (error) {
      return "";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("archivedprojects_searchQuery", JSON.stringify(archivedSearchQuery));
    } catch (error) {
      console.error("Failed to save archived search query", error);
    }
  }, [archivedSearchQuery]);

  // The controls (Filter / Columns / Search) and the Outlet bind to whichever tab is active.
  const currentFilters = isArchivedTab ? archivedFilters : activeFilters;
  const applyCurrentFilters = (f) => (isArchivedTab ? setArchivedFilters(f) : setActiveFilters(f));
  const clearCurrentFilters = () => applyCurrentFilters(makeDefaultFilters());

  const currentSearch = isArchivedTab ? archivedSearchQuery : searchQuery;
  const setCurrentSearch = isArchivedTab ? setArchivedSearchQuery : setSearchQuery;

  const tabs = [
    { name: "Projects", path: "my-projects" },
    { name: "Archived Projects", path: "archived-projects" },
  ];

  return (
    <div className="flex flex-col h-full w-full font-outfit overflow-hidden bg-[#FAFAFA]">
      <div className="flex-shrink-0 bg-white">
        <Header
          name="Projects"
          icon={""}
          isMenuOpen={isMenuOpen}
          isKnowledgeBasePage={false}
          headerRightContent={<div id="projects-header-portal"></div>}
        />
      </div>

      {/* Tab Navigation & Controls */}
      <div className="flex justify-between items-center px-6 pt-2 pb-0  bg-white flex-shrink-0 w-full">
        <div className="flex items-center gap-4 border-b-[1px]">
          {tabs.map((tab) => {
            const isActive = currentPath.includes(tab.path.toLowerCase());
            return (
              <button
                key={tab.name}
                onClick={() => navigate(tab.path)}
                className={`font-outfit text-[15px] pb-0 px-1 transition-colors ${
                  isActive
                    ? "text-[#BB42AE] font-semibold border-b-[2px] border-[#BB42AE]"
                    : "text-[#707070] font-medium border-transparent hover:text-[#C95EBE]"
                }`}
              >
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Right Side Controls (Filter, Columns, Search) */}
        <div className="flex items-center gap-3 pb-3">
          <div className="relative inline-flex items-center gap-2 z-[50]">
            <MyProjectFilterDropdown
              key={activeTab}
              initialFilters={currentFilters}
              onApply={applyCurrentFilters}
              onClear={clearCurrentFilters}
              tagsRefreshTrigger={tagsRefreshTrigger}
              // My access only means something on the merged Projects tab -
              // Archived doesn't mix sources.
              showSourceFilter={!isArchivedTab}
              showOrgSource={hasOrgProjects}
            />
          </div>

          <div className={`relative inline-flex items-center gap-2 ${isArchivedTab ? "hidden" : ""}`}>
            <button
              onClick={() => setIsColumnDropdownOpen((p) => !p)}
              className={`flex items-center gap-2 px-3 py-1.5 border rounded text-[14px] font-outfit transition-colors ${
                isColumnDropdownOpen
                  ? "border-[#BB42AE] text-[#BB42AE] bg-[#F8F3FF]"
                  : "border-[#00000036] text-[#52525B] hover:bg-gray-50"
              }`}
            >
              <ColumnIcon /> Columns
            </button>
            {isColumnDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-[998] bg-transparent"
                  onClick={() => setIsColumnDropdownOpen(false)}
                />
                <ProjectColumnDropdown
                  key={activeTab}
                  columns={columnPreferences}
                  setColumns={setColumnPreferences}
                  onSave={saveColumnPreferences}
                  onClose={() => setIsColumnDropdownOpen(false)}
                  storageKey="projects_columnPreferences"
                />
              </>
            )}
          </div>

          <div className="relative w-[250px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
              size={18}
            />
            <input
              type="text"
              placeholder="Search projects..."
              value={currentSearch}
              onChange={(e) => setCurrentSearch(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className={`w-full pl-10 pr-4 border rounded-md focus:outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE] box-border h-[35px] text-[14px] leading-[20px] transition-all ${
                isSearchFocused ? "border-[#C95EBE] shadow-sm" : "border-[#00000036]"
              }`}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden bg-[#fafafa]">
        {/* Render Outlet immediately. No loading blocks! */}
        <Outlet
          context={{
            searchQuery: currentSearch,
            activeFilters: currentFilters,
            columnPreferences,
          }}
        />
      </div>

      <div
        id="projects-footer-portal"
        className="w-full bg-white border-t border-black/10 flex-shrink-0 z-10 empty:hidden"
      ></div>
    </div>
  );
};

export default ProjectsLayout;